/**
 * The language a NEW TENANT is created in.
 *
 * The setup wizard sends this so the workspace inherits the language its
 * creator was actually using. Without it the backend falls back to the company
 * COUNTRY, which answers the wrong question: an Israeli company whose founder
 * signs up entirely in English got a Hebrew workspace. See
 * `docs/platform/i18n-language-resolution-design.md` § New tenant
 * `default_language`.
 *
 * `tenants.default_language` used to accept a NARROWER set than the app's UI
 * languages, so a wizard completed in Chinese sent `null` and the founder got
 * an English workspace. It no longer does (WI 82d94165): the column stores
 * every shipped UI language, and this function's remaining job is to strip a
 * region subtag and refuse a code the app does not ship at all — a stale
 * cross-subdomain cookie, or a hand-written API call.
 */
import { UI_LANGUAGE_VALUES } from '@/generated/config-enums';
import type { CreateTenantRequestDefaultLanguage } from '@/generated/api/models/createTenantRequestDefaultLanguage';

type TenantLanguage = NonNullable<CreateTenantRequestDefaultLanguage>;

// The config's own value set, checked against the generated API union: the two
// are generated from different sources (the config enum vs. the OpenAPI schema
// the route model produces), so a language the column stops accepting fails
// tsc HERE rather than becoming a 422 at the end of the wizard.
const TENANT_LANGUAGES = UI_LANGUAGE_VALUES satisfies readonly TenantLanguage[];

/**
 * Narrow an i18next language code to what `tenants.default_language` accepts.
 *
 * @param language The active UI language (e.g. `i18n.language`, which may carry
 *   a region subtag such as `en-US`).
 * @returns The base language code, or `null` when the column cannot hold it.
 */
export function toTenantDefaultLanguage(
  language: string | undefined | null,
): TenantLanguage | null {
  const base = language?.split('-')[0];
  return TENANT_LANGUAGES.find((code) => code === base) ?? null;
}
